import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

import Portal from "./Portal.jsx";
import { useSelector, useDispatch } from "react-redux";
import { portalActions } from "../store/index.js";
import { logout } from "../api/authApi";

import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import Avatar from "@mui/material/Avatar";
import ListItemIcon from "@mui/material/ListItemIcon";
import Settings from "@mui/icons-material/Settings";
import Logout from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const Navbar = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const user = useSelector((state) => state.auth.user);
  const portalStates = useSelector((state) => state.portal);

  const open = Boolean(anchorEl);
  const handleDropDownClose = () => {
    setAnchorEl(null);
    document.body.classList.remove("account-menu-custom-style");
  };

  const takeToHomePage = () => {
    if (isLoggedIn && user?.username) {
      navigate(`/${user.username}/dashboard`);
    } else {
      navigate(`/`);
    }
  };

  const takeToExercisesPage = () => {
    navigate(`/exercises/all`);
  };

  const sendLogoutReq = async () => {
    await logout(dispatch);
    navigate(`/`);
    handleDropDownClose();
  };

  const handleAvatarClick = (e) => {
    e.stopPropagation();
    navigate(`/${user?.username}/myprofile`);
  };

  const handleDropdownToggle = (event) => {
    event.stopPropagation();
    if (anchorEl) {
      handleDropDownClose();
    } else {
      const container = event.currentTarget.closest(".avatar-pill-container") || event.currentTarget;
      setAnchorEl(container);
      document.body.classList.add("account-menu-custom-style");
    }
  };

  const [navVisible, setNavVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  function handleLogout() {
    sendLogoutReq();
  }

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY <= 20) {
        setNavVisible(true);
        document.body.classList.remove("navbar-hidden");
      } else if (currentScrollY > lastScrollY && currentScrollY > 120) {
        setNavVisible(false);
        document.body.classList.add("navbar-hidden");
      } else if (currentScrollY < lastScrollY) {
        setNavVisible(true);
        document.body.classList.remove("navbar-hidden");
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.body.classList.remove("navbar-hidden");
    };
  }, [lastScrollY]);

  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const saved = localStorage.getItem("sidebar-shown");
    return saved !== null ? saved === "true" : true;
  });

  useEffect(() => {
    const handleSidebarChange = (e) => {
      if (e.detail?.isSidebarShown !== undefined) {
        setIsSidebarOpen(Boolean(e.detail.isSidebarShown));
      } else {
        const saved = localStorage.getItem("sidebar-shown");
        setIsSidebarOpen(saved !== null ? saved === "true" : true);
      }
    };
    window.addEventListener("sidebar-state-change", handleSidebarChange);
    return () => window.removeEventListener("sidebar-state-change", handleSidebarChange);
  }, []);

  return (
    <div>
      <nav id="nav" className={!navVisible ? "nav-hidden" : ""}>
        <div className="nav-brand-group">
          {isLoggedIn && !isMobile && (
            <button
              className={`navbar-hamburger-btn ${isSidebarOpen ? "active" : ""}`}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                window.dispatchEvent(new CustomEvent("toggle-sidebar"));
              }}
              title={isSidebarOpen ? "Collapse Sidebar" : "Expand Sidebar"}
            >
              {isSidebarOpen ? <MenuOpenIcon /> : <MenuIcon />}
            </button>
          )}
          <h2 onClick={takeToHomePage} id="heading">
            FitHub
          </h2>
        </div>

        <div className="links">
          {!isMobile && (
            <>
              <h3 onClick={takeToExercisesPage} className="link">
                Exercises
              </h3>
              <h3
                onClick={() => navigate(`/workouts?tab=explore`)}
                className="link"
              >
                Explore Workouts
              </h3>
              <h3 onClick={() => navigate(`/recipes`)} className="link">
                Recipes
              </h3>
            </>
          )}

          {!isLoggedIn ? (
            <button
              onClick={() => dispatch(portalActions.setPortalOpen())}
              className="login-btn"
            >
              Login
            </button>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              {isLoggedIn && (
                <div
                  className={`navbar-streak-badge ${user?.streak > 0 ? "lit" : ""}`}
                  onClick={() => navigate(`/${user?.username}/dashboard`)}
                  title={user?.streak > 0 ? `${user.streak} Day Workout Streak! 🔥` : "Start a streak — workout today!"}
                >
                  <WhatshotIcon style={{ fontSize: "1.15rem" }} />
                  <span>
                    {user?.streak || 0}
                  </span>
                </div>
              )}
              <div
                className={`avatar-pill-container ${open ? "active" : ""}`}
                onClick={handleDropdownToggle}
                aria-controls={open ? "account-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
              >
                <Tooltip title="View Profile">
                  <Avatar
                    className="profile-avatar"
                    onClick={handleAvatarClick}
                    style={{ cursor: "pointer" }}
                    title="Go to My Profile"
                  >
                    {user?.username ? user.username[0].toUpperCase() : "U"}
                  </Avatar>
                </Tooltip>
                {!isMobile && (
                  <span className="avatar-username" title="Account Menu">
                    {user?.username}
                  </span>
                )}
                <KeyboardArrowDownIcon
                  className="avatar-dropdown-arrow"
                  title="Account Menu"
                />
              </div>
              <Menu
                anchorEl={anchorEl}
                id="account-menu"
                className="account-menu"
                open={open}
                onClick={handleDropDownClose}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                <div
                  style={{
                    padding: "10px 16px 8px",
                    borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.95rem",
                      fontWeight: "700",
                      color: "#fff",
                    }}
                  >
                    {user?.fullname || user?.username}
                  </div>
                  <div style={{ fontSize: "0.78rem", color: "#94a3b8" }}>
                    @{user?.username}
                  </div>
                </div>

                <MenuItem
                  onClick={() => {
                    handleDropDownClose();
                    navigate(`/${user?.username}/myprofile`);
                  }}
                  style={{ marginTop: "4px" }}
                >
                  <ListItemIcon>
                    <PersonIcon className="account-menu-dropdown-icon" fontSize="small" />
                  </ListItemIcon>
                  <span>My Profile & Goals</span>
                </MenuItem>

                <MenuItem
                  onClick={() => {
                    handleDropDownClose();
                    navigate(`/${user?.username}/settings`);
                  }}
                >
                  <ListItemIcon>
                    <Settings className="account-menu-dropdown-icon" fontSize="small" />
                  </ListItemIcon>
                  <span>Settings & Units</span>
                </MenuItem>

                <Divider className="line" style={{ margin: "6px 0" }} />

                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <Logout
                      className="account-menu-dropdown-icon"
                      fontSize="small"
                      style={{ color: "#ff5e62" }}
                    />
                  </ListItemIcon>
                  <span style={{ color: "#ff5e62" }}>Logout</span>
                </MenuItem>
              </Menu>
            </div>
          )}
          {portalStates.isPortalOpen && <Portal />}
        </div>
      </nav>
    </div>
  );
};

export default Navbar;
